import { useEffect, useMemo, useState, useCallback } from "react";
import axiosInstance from "../../../../Config/axiosInstance";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import "./ProjectCodeDialog.css";

const animatedComponents = makeAnimated();

const PREVIEW_URL = "/subprojectcodes/preview/";
const CREATE_URL = "/subprojectcodes/";
const OPPORTUNITIES_URL = "/opportunities/";

export default function SubProjectCodeDialog({
  open,
  onClose,
  onCreated,
  defaultProjectCode,
}) {
  const [departments, setDepartments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [loadingLists, setLoadingLists] = useState(false);

  const [clientsMap, setClientsMap] = useState({});

  const [department, setDepartment] = useState(null);
  const [activity, setActivity] = useState(null);
  const [opportunity, setOpportunity] = useState(null);
  const [description, setDescription] = useState("");

  const [, setNr] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [clientName, setClientName] = useState("");
  const [code, setCode] = useState("");

  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [errors, setErrors] = useState({
    department: "",
    activity: "",
    description: "",
    opportunity: "",
    code: "",
    submit: "",
  });

  const setSubmitError = (e) => {
    const msg =
      typeof e?.response?.data === "string"
        ? `${e.response.status} ${e.response.statusText}`
        : e?.response?.data
          ? JSON.stringify(e.response.data)
          : e?.message || "Unknown error";

    setErrors((prev) => ({ ...prev, submit: msg }));
  };

  const projectCodeLabel = useMemo(() => {
    if (!defaultProjectCode) return "";
    return defaultProjectCode.code ?? `#${defaultProjectCode.id}`;
  }, [defaultProjectCode]);

  const getOrganizationLabel = useCallback((pc) => {
    if (!pc) return "";

    return (
      pc.organization_name ??
      pc.organization_label ??
      pc.organization?.name ??
      pc.organization_details?.name ??
      ""
    );
  }, []);

  const getClientLabel = useCallback((pc, cmap) => {
    if (!pc) return "";

    const clientId = pc.client;

    return (
      pc.client_name ??
      pc.client_label ??
      pc.client_details?.name ??
      pc.client?.name ??
      cmap?.[clientId] ??
      ""
    );
  }, []);

  useEffect(() => {
    if (!open) return;

    setDepartment(null);
    setActivity(null);
    setOpportunity(null);
    setDescription("");

    setNr("");
    setOrganizationName("");
    setClientName("");
    setCode("");

    setSaving(false);
    setGenerating(false);
    setSubmitted(false);

    setErrors({
      department: "",
      activity: "",
      description: "",
      opportunity: "",
      code: "",
      submit: "",
    });

    const loadLists = async () => {
      setLoadingLists(true);

      try {
        const [depRes, actRes, oppRes, cliRes] = await Promise.all([
          axiosInstance.get("/departments/"),
          axiosInstance.get("/code_matrix/"),
          axiosInstance.get(OPPORTUNITIES_URL),
          axiosInstance.get("/clients/"),
        ]);

        setDepartments(depRes?.data || []);
        setActivities(actRes?.data || []);
        setOpportunities(oppRes?.data || []);

        const clients = cliRes?.data || [];
        const cmap = clients.reduce((acc, c) => {
          acc[c.id] = c.name ?? c.denumire ?? String(c.id);
          return acc;
        }, {});

        setClientsMap(cmap);

        if (defaultProjectCode?.id) {
          setNr(String(defaultProjectCode.nr ?? ""));

          const orgLabel = getOrganizationLabel(defaultProjectCode);
          const cliLabel = getClientLabel(defaultProjectCode, cmap);

          setOrganizationName(String(orgLabel));
          setClientName(String(cliLabel));
        }
      } catch (e) {
        setSubmitError(e);
      } finally {
        setLoadingLists(false);
      }
    };

    loadLists();
  }, [open, defaultProjectCode, getOrganizationLabel, getClientLabel]);

  useEffect(() => {
    if (!open) return;
    setOpportunity(null);
  }, [open, defaultProjectCode?.id]);

  const departmentOptions = useMemo(
    () =>
      (departments || []).map((d) => ({
        value: d.id,
        label: `${d.name}${d.department_abbreviated ? ` (${d.department_abbreviated})` : ""
          }`,
        __raw: d,
      })),
    [departments]
  );

  const activityOptions = useMemo(() => {
    const depId = department?.value;

    const filtered = depId
      ? (activities || []).filter((m) => m.department === depId)
      : activities || [];

    return filtered.map((m) => ({
      value: m.id,
      label: `${m.activity}${m.activity_abbreviated ? ` (${m.activity_abbreviated})` : ""
        }`,
      __raw: m,
    }));
  }, [activities, department]);

  const opportunityOptions = useMemo(() => {
    const pcId = defaultProjectCode?.id;

    const filtered = pcId
      ? (opportunities || []).filter((o) => {
        const oppPcId = o.project_code_id ?? o.project_code;

        const spId =
          o.subproject_code_id ??
          o.subproject_code?.id ??
          o.subproject_code ??
          null;

        const isSameParent = Number(oppPcId) === Number(pcId);
        const isFree = spId === null || spId === "" || Number(spId) === 0;

        return isSameParent && isFree;
      })
      : [];

    return filtered.map((o) => ({
      value: o.id,
      label: `${o.code_opportunity ?? ""} — ${o.project_name ?? o.title_opportunity ?? `Opportunity #${o.id}`
        }`,
      department_id:
        o.department_id ??
        o.department ??
        o.department?.id ??
        null,
      __raw: o,
    }));
  }, [opportunities, defaultProjectCode]);

  const validateAll = useCallback(() => {
    const next = {
      department: "",
      activity: "",
      description: "",
      opportunity: "",
      code: "",
      submit: "",
    };

    if (!defaultProjectCode?.id) next.submit = "Project code is missing.";
    if (!department) next.department = "Required";
    if (!activity) next.activity = "Required";
    if (!description.trim()) next.description = "Required";
    if (!opportunity) next.opportunity = "Required";
    if (!code.trim()) next.code = "Required";

    setErrors((prev) => ({ ...prev, ...next }));

    return (
      !next.submit &&
      !next.department &&
      !next.activity &&
      !next.description &&
      !next.opportunity &&
      !next.code
    );
  }, [defaultProjectCode, department, activity, description, opportunity, code]);

  useEffect(() => {
    if (!open) return;
    if (!defaultProjectCode?.id) return;

    const orgLabel = getOrganizationLabel(defaultProjectCode);
    const cliLabel = getClientLabel(defaultProjectCode, clientsMap);

    setOrganizationName(String(orgLabel));
    setClientName(String(cliLabel));
    setNr(String(defaultProjectCode.nr ?? ""));
  }, [
    open,
    defaultProjectCode,
    clientsMap,
    getOrganizationLabel,
    getClientLabel,
  ]);

  useEffect(() => {
    if (!open) return;

    if (!defaultProjectCode?.id || !activity?.value) {
      setCode("");
      return;
    }

    const t = setTimeout(async () => {
      setGenerating(true);
      setErrors((prev) => ({ ...prev, submit: "" }));

      try {
        const res = await axiosInstance.post(PREVIEW_URL, {
          project_code: defaultProjectCode.id,
          matrix: activity.value,
        });

        const generatedNr = res.data?.nr ?? "";
        const generatedCode = res.data?.code ?? "";

        if (generatedNr) setNr(String(generatedNr));
        setCode(String(generatedCode));

        if (generatedCode) {
          setErrors((prev) => ({ ...prev, code: "" }));
        }
      } catch (e) {
        setCode("");
        setSubmitError(e);
      } finally {
        setGenerating(false);
      }
    }, 250);

    return () => clearTimeout(t);
  }, [open, defaultProjectCode, activity?.value]);

  const handleSubmit = async () => {
    setSubmitted(true);
    setErrors((prev) => ({ ...prev, submit: "" }));

    if (!validateAll()) return;

    setSaving(true);

    try {
      await axiosInstance.post(CREATE_URL, {
        project_code: defaultProjectCode.id,
        matrix: activity.value,
        opportunity_id: opportunity?.value ?? null,
        description: description.trim(),
        status: "Active",
      });

      onCreated?.();
      onClose?.();
    } catch (e) {
      setSubmitError(e);
    } finally {
      setSaving(false);
    }
  };

  const disabledAll = saving || generating || loadingLists;

  const showDeptError = submitted && !!errors.department;
  const showActError = submitted && !!errors.activity;
  const showDescError = submitted && !!errors.description;
  const showOppError = submitted && !!errors.opportunity;
  const showCodeError = submitted && !!errors.code;

  return (
    <Dialog
      open={open}
      onClose={disabledAll ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      className="project-code-dialog"
    >
      <DialogTitle className="spc-title">
        <div className="spc-header">
          <div className="spc-header-top">
            <span className="spc-header-title">Create Sub Project Code</span>
          </div>

          <div className="spc-header-sub">
            <span className="spc-header-sub-label">Project</span>
            <span className="spc-header-sub-sep">:</span>
            <span className="spc-header-sub-value">{projectCodeLabel}</span>
          </div>
        </div>
      </DialogTitle>

      <DialogContent dividers className="cmd-content">
        {/* Organization */}
        <div className="cmd-form-group">
          <label className="cmd-label">Organization</label>

          <input
            type="text"
            className="cmd-input"
            value={organizationName}
            readOnly
            disabled
            placeholder="Auto from project code"
          />
        </div>

        {/* Client */}
        <div className="cmd-form-group">
          <label className="cmd-label">Client</label>

          <input
            type="text"
            className="cmd-input"
            value={clientName}
            readOnly
            disabled
            placeholder="Auto from project code"
          />
        </div>

        {/* Opportunity */}
        <div className="cmd-form-group">
          <label className="cmd-label">
            Opportunity <span style={{ color: "red" }}>*</span>
          </label>

          <Select
            options={opportunityOptions}
            value={opportunity}
            onChange={(val) => {
              setOpportunity(val);

              if (!val) {
                setDepartment(null);
                setActivity(null);
                setCode("");

                setErrors((prev) => ({
                  ...prev,
                  opportunity: submitted ? "Required" : "",
                  department: submitted ? "Required" : "",
                  activity: submitted ? "Required" : "",
                  code: submitted ? "Required" : "",
                }));

                return;
              }

              setErrors((prev) => ({ ...prev, opportunity: "" }));

              const depId = val.department_id;

              if (depId) {
                const depOption = departmentOptions.find(
                  (d) => String(d.value) === String(depId)
                );

                if (depOption) {
                  setDepartment(depOption);
                  setActivity(null);
                  setCode("");

                  setErrors((prev) => ({
                    ...prev,
                    department: "",
                    activity: "",
                    code: "",
                  }));
                }
              }
            }}
            isClearable
            isSearchable
            placeholder="Select opportunity"
            classNamePrefix="cmd-react-select"
            components={animatedComponents}
            isDisabled={disabledAll}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 20000 }),
              menu: (base) => ({ ...base, zIndex: 20000 }),
            }}
            className={showOppError ? "cmd-select-error" : ""}
          />

          {showOppError && (
            <div className="cmd-field-error">{errors.opportunity}</div>
          )}
        </div>

        {/* Department */}
        <div className="cmd-form-group">
          <label className="cmd-label">
            Department <span style={{ color: "red" }}>*</span>
          </label>

          <Select
            options={departmentOptions}
            value={department}
            onChange={() => { }}
            isClearable={false}
            isSearchable={false}
            placeholder="Auto from opportunity"
            classNamePrefix="cmd-react-select"
            components={{
              DropdownIndicator: null,
              IndicatorSeparator: null,
            }}
            isDisabled
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 20000 }),
              menu: (base) => ({ ...base, zIndex: 20000 }),
            }}
            className={showDeptError ? "cmd-select-error" : ""}
          />

          {showDeptError && (
            <div className="cmd-field-error">{errors.department}</div>
          )}
        </div>

        {/* Activity */}
        <div className="cmd-form-group">
          <label className="cmd-label">
            Activity <span style={{ color: "red" }}>*</span>
          </label>

          <Select
            options={activityOptions}
            value={activity}
            onChange={(val) => {
              setActivity(val);
              if (val) setErrors((prev) => ({ ...prev, activity: "" }));
            }}
            onBlur={() => {
              if (!submitted) return;
              setErrors((prev) => ({
                ...prev,
                activity: activity ? "" : "Required",
              }));
            }}
            isClearable
            isSearchable
            placeholder={
              department ? "Select activity" : "First select a department"
            }
            classNamePrefix="cmd-react-select"
            components={animatedComponents}
            isDisabled={disabledAll || !department}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 20000 }),
              menu: (base) => ({ ...base, zIndex: 20000 }),
            }}
            className={showActError ? "cmd-select-error" : ""}
          />

          {showActError && (
            <div className="cmd-field-error">{errors.activity}</div>
          )}
        </div>

        {/* Code */}
        <div className="cmd-form-group">
          <label className="cmd-label">
            Code <span style={{ color: "red" }}>*</span>
          </label>

          <input
            type="text"
            className={`cmd-input ${showCodeError ? "cmd-input-error" : ""}`}
            placeholder="Generated automatically"
            value={code}
            readOnly
            disabled
          />

          {generating && (
            <div style={{ marginTop: 6 }}>
              <CircularProgress size={18} />
            </div>
          )}

          {showCodeError && (
            <div className="cmd-field-error">{errors.code}</div>
          )}
        </div>

        {/* Description */}
        <div className="cmd-form-group">
          <label className="cmd-label">
            Description <span style={{ color: "red" }}>*</span>
          </label>

          <textarea
            rows={6}
            className={`cmd-input ${showDescError ? "cmd-input-error" : ""}`}
            placeholder="Enter description..."
            value={description}
            onChange={(e) => {
              const val = e.target.value;
              setDescription(val);

              if (val.trim()) {
                setErrors((prev) => ({ ...prev, description: "" }));
              }
            }}
            onBlur={() => {
              if (!submitted) return;

              setErrors((prev) => ({
                ...prev,
                description: description.trim() ? "" : "Required",
              }));
            }}
            disabled={disabledAll}
            style={{ resize: "vertical" }}
          />

          {showDescError && (
            <div className="cmd-field-error">{errors.description}</div>
          )}
        </div>

        {/* Status */}
        <div className="cmd-form-group">
          <label className="cmd-label">Status</label>

          <input
            type="text"
            className="cmd-input"
            value="Active"
            readOnly
            disabled
          />
        </div>

        {errors.submit && (
          <div className="cmd-submit-error">{errors.submit}</div>
        )}
      </DialogContent>

      <DialogActions className="cmd-actions">
        <Button
          onClick={onClose}
          disabled={disabledAll}
          className="cmd-btn cmd-btn-cancel"
        >
          Cancel
        </Button>

        <Button
          onClick={handleSubmit}
          disabled={disabledAll}
          variant="contained"
          className="cmd-btn cmd-btn-create"
          startIcon={
            saving ? <CircularProgress size={18} className="cmd-spinner" /> : null
          }
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}